import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchDramas, getHotDramas } from "@/utils/content";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const typeTabs = ["全部", "电视剧", "电影", "综艺"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [type, setType] = useState("全部");

  const results = useMemo(() => searchDramas(keyword), [keyword]);
  const filtered = type === "全部" ? results : results.filter((item) => item.category === type);
  const hot = getHotDramas(4);

  const submit = (value: string) => {
    const key = value.trim();
    if (!key) return;
    setSearchParams({ q: key });
    setType("全部");
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <title>{keyword ? `“${keyword}”搜索结果 - 善良的嫂子影视站` : "搜索 - 善良的嫂子影视站"}</title>
      <meta
        name="description"
        content={`在善良的嫂子影视站搜索“${keyword}”相关的影视剧、演员与资源，快速定位你想了解的内容。`}
      />
      <meta name="keywords" content="影视搜索,搜索结果,善良的嫂子,影视剧查询" />

      <Breadcrumbs items={[{ label: "搜索结果" }]} />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit(input);
        }}
        className="mt-4 flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2"
      >
        <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
          <i className="ri-search-line text-lg"></i>
        </span>
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="输入剧名、演员名或关键词"
          className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
        />
        <button
          type="submit"
          className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {keyword ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
            <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              关键词 <span className="text-primary-600">“{keyword}”</span> 的搜索结果
            </h1>
            <span className="text-sm text-foreground-500">
              共找到 <strong className="text-primary-600">{results.length}</strong> 条结果
            </span>
          </div>

          {results.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1 w-fit">
              {typeTabs.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setType(item)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    type === item ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
              {filtered.map((drama) => (
                <DramaCard key={drama.id} drama={drama} variant="list" />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-lg border border-background-200 bg-background-50 py-16">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">没有找到匹配的影视内容</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面这些热门推荐</p>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-background-200 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
            <i className="ri-search-line text-3xl"></i>
          </span>
          <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">输入关键词开始搜索</p>
          <p className="mt-1 text-sm text-foreground-500">支持剧名、演员名、类型等关键词检索</p>
        </div>
      )}

      {(filtered.length === 0 || !keyword) && (
        <div className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950">热门影视推荐</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            {hot.map((drama) => (
              <DramaCard key={drama.id} drama={drama} variant="list" />
            ))}
          </div>
          <div className="mt-6">
            <Link to="/category" className="text-sm font-medium text-primary-600 hover:text-primary-700">
              浏览全部影视 →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}